import {
    clearKeyword,
    clearSelection,
    setKeyword,
    setPage,
    setPageSize,
} from '../reducers';

import {
    selectionSelector,
    selectVisibility,
    visibilityStateSelector,
} from '../selectors';

import {columns} from '../tables/columns';
import {
    useAppDispatch,
    useAppSelector
} from '../../../../hooks/useTypedReactRedux';

import {
    useDeleteOrganizationMutation,
    useOrganizationsQuery,
} from './useOrganizationsQuery';

// FNP-948: records come from React Query; selection, paging and keyword stay in
// Redux. Return shape unchanged so the table, buttons, inputs and pages
// consuming this hook did not change.
//
// The filter is a HOOK PARAMETER rather than an argument to fetchRecords,
// because it belongs in the query key: IndexPage scopes the list by
// adminAccountId, and that must not share a cache entry with the unscoped list.
// fetchRecords() still accepts a query argument for call-site compatibility,
// but it only triggers a refetch - it does not change what is fetched.
export const useOrganizations = (filter: { [k: string]: string } = {}) => {

    const dispatch = useAppDispatch();

    const selection = useAppSelector((state) => selectionSelector(state));
    const visibility = useAppSelector((state) => visibilityStateSelector(state));

    const {
        data,
        isLoading,
        refetch
    } = useOrganizationsQuery(filter);

    const deleteMutation = useDeleteOrganizationMutation();

    const {
        items,
        visibleItems,
        limit,
        keyword,
        page,
        totalPages,
        visibleCount,
    } = selectVisibility(visibility, data ?? []);

    // FETCH
    const fetchRecords = (_query?: { [k: string]: string }) => {
        refetch();
    };

    const deleteRecords = () => {
        if (Object.keys(selection).length > 0) {
            for (const key in selection) {
                deleteMutation.mutate(key);
            }
            // Clear the checkboxes once the deletions are issued - otherwise the
            // selection keeps naming records that no longer exist.
            dispatch(clearSelection());
        }
    };


    // FILTERING AND PAGING
    const handleFilterByKeyword = (keyword: string) => {
        if (keyword !== '') {
            dispatch(setKeyword(keyword));
        } else {
            dispatch(clearKeyword());
        }
    };

    const handleItemsPerPageChange = (pageSize: string | null) => {
        dispatch(setPageSize(pageSize));
    };

    const handlePageChange = (page: number) => {
        dispatch(setPage(page));
    };

    // BATCH ACTIONS
    const handleBatchActionCancel = () => {
        dispatch(clearSelection());
    };

    return {
        items,
        visibleItems,
        limit,
        loading: isLoading,
        keyword,
        page,
        totalPages,
        visibleCount,
        columns,
        fetchRecords,
        deleteRecords,
        handleFilterByKeyword,
        handleItemsPerPageChange,
        handleBatchActionCancel,
        handlePageChange,
    };
};
